import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import { getMarketStatus } from '../utils/marketStatus';

const WS_URL = process.env.REACT_APP_WS_URL || `wss://${window.location.host}/ws`;

const MarketDataContext = createContext(null);

export function MarketDataProvider({ children }) {
  const [tickers, setTickers] = useState({});
  const [connected, setConnected] = useState(false);
  const [marketStatus, setMarketStatus] = useState(getMarketStatus());
  const [shortSymbols, setShortSymbols] = useState(new Set());
  const [breakoutLongs, setBreakoutLongs] = useState([]);
  const [breakoutShorts, setBreakoutShorts] = useState([]);
  const [mainBreakoutLongs, setMainBreakoutLongs] = useState([]);
  const [mainBreakoutShorts, setMainBreakoutShorts] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);

  // Update market status every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketStatus(getMarketStatus());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    const token = await getValidToken();
    if (!token || !mountedRef.current) return;

    const ws = new WebSocket(`${WS_URL}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const messages = JSON.parse(event.data);

        // Handle breakout_signals event
        if (messages && !Array.isArray(messages) && messages.ev === 'breakout_signals') {
          setBreakoutLongs(messages.longs || []);
          setBreakoutShorts(messages.shorts || []);
          setMainBreakoutLongs(messages.main_longs || []);
          setMainBreakoutShorts(messages.main_shorts || []);
          return;
        }

        // Handle short_symbols event (object, not array)
        if (messages && !Array.isArray(messages) && messages.ev === 'short_symbols') {
          setShortSymbols(new Set(messages.symbols));
          return;
        }

        if (!Array.isArray(messages)) return;

        setTickers((prev) => {
          const updated = { ...prev };
          for (const msg of messages) {
            if (msg.ev !== 'AM') continue;
            if (!msg.op || msg.op <= 0) continue;

            const changePercent = ((msg.c - msg.op) / msg.op) * 100;

            updated[msg.sym] = {
              sym: msg.sym,
              price: msg.c,
              open: msg.op,
              changePercent: changePercent,
              volume: msg.v,
              timestamp: msg.s,
              premarketOpen: msg.pm_op || 0,
              mainOpen: msg.mm_op || 0,
            };
          }
          return updated;
        });
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setConnected(false);
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  return (
    <MarketDataContext.Provider value={{ tickers, connected, marketStatus, shortSymbols, breakoutLongs, breakoutShorts, mainBreakoutLongs, mainBreakoutShorts }}>
      {children}
    </MarketDataContext.Provider>
  );
}

export function useMarketContext() {
  const context = useContext(MarketDataContext);
  if (!context) {
    throw new Error('useMarketContext must be used within MarketDataProvider');
  }
  return context;
}
